import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { merchantsApi, industriesApi, businessCategoriesApi, staffApi } from '../api/merchantsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreateMerchantPayload, UpdateMerchantPayload, InviteMerchantPayload, UpdateMerchantUserPayload } from '../types';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

export const useMerchant = (id: string) =>
  useQuery({ queryKey: ['merchants', id], queryFn: () => merchantsApi.get(id), staleTime: 2 * 60 * 1000, enabled: !!id });

export const useMerchantApiKey = (id: string) =>
  useQuery({ queryKey: ['merchants', id, 'api-key'], queryFn: () => merchantsApi.getApiKey(id), staleTime: 0 });

export const useMerchantUsers = (id: string) =>
  useQuery({ queryKey: ['merchants', id, 'users'], queryFn: () => merchantsApi.listUsers(id), staleTime: 2 * 60 * 1000 });

export const useMerchants = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({ queryKey: ['merchants'], queryFn: merchantsApi.list, staleTime: 2 * 60 * 1000, enabled });

export const useSearchMerchants = (query: string) =>
  useQuery({
    queryKey: ['merchants', 'search', query],
    queryFn: () => merchantsApi.search(query, 20),
    enabled: query.length >= 1,
    staleTime: 30 * 1000,
  });

export const useMerchantsPreview = (limit = 5) =>
  useQuery({
    queryKey: ['merchants', 'preview', limit],
    queryFn: () => merchantsApi.listFirst(limit),
    staleTime: 5 * 60 * 1000,
  });

export const useIndustries = () =>
  useQuery({ queryKey: ['industries'], queryFn: industriesApi.list, staleTime: 10 * 60 * 1000 });

export const useBusinessCategories = () =>
  useQuery({ queryKey: ['businessCategories'], queryFn: businessCategoriesApi.list, staleTime: 10 * 60 * 1000 });

export const useTCSStaff = () =>
  useQuery({ queryKey: ['tcs-staff'], queryFn: staffApi.list, staleTime: 5 * 60 * 1000 });

export const useInviteMerchant = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: InviteMerchantPayload) => merchantsApi.invite(data),
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to send invite')),
  });
};

export const useCreateMerchant = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateMerchantPayload) => merchantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      toast.success('Merchant created successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create merchant')),
  });
};

export const useUpdateMerchant = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMerchantPayload }) => merchantsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      toast.success('Merchant updated successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update merchant')),
  });
};

export const useGenerateApiKey = (merchantId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: () => merchantsApi.generateApiKey(merchantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchants', merchantId, 'api-key'] }),
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to generate API key')),
  });
};

export const useRevokeApiKey = (merchantId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: () => merchantsApi.revokeApiKey(merchantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants', merchantId, 'api-key'] });
      toast.success('API key revoked');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to revoke API key')),
  });
};

export const useToggleMerchantStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? merchantsApi.deactivate(id) : merchantsApi.activate(id),
    onSuccess: (_, { active }) => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      toast.success(`Merchant ${active ? 'deactivated' : 'activated'} successfully`);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update merchant status')),
  });
};

export const useMerchantApiKeysHistory = (id: string) =>
  useQuery({ queryKey: ['merchants', id, 'api-keys-history'], queryFn: () => merchantsApi.listApiKeysHistory(id), staleTime: 0 });

export const useMerchantMetrics = (id: string) =>
  useQuery({ queryKey: ['merchants', id, 'metrics'], queryFn: () => merchantsApi.getMetrics(id), staleTime: 30 * 1000 });

export const useMerchantPauseStatus = (id: string) =>
  useQuery({ queryKey: ['merchants', id, 'pause-status'], queryFn: () => merchantsApi.getPauseStatus(id), staleTime: 10 * 1000 });

export const useToggleMerchantPause = (merchantId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (isPaused: boolean) => merchantsApi.setPauseStatus(merchantId, isPaused),
    onSuccess: (_, isPaused) => {
      queryClient.invalidateQueries({ queryKey: ['merchants', merchantId, 'pause-status'] });
      toast.success(`Ordering ${isPaused ? 'paused' : 'unpaused'} successfully`);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update pause status')),
  });
};

export const useResendInvite = (merchantId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: () => merchantsApi.resendInvite(merchantId),
    onSuccess: () => {
      // Refetch so the new lastInvitedAt (and any other invite state) shows immediately —
      // without this the toast fires but the page looks unchanged, which reads as "nothing happened".
      queryClient.invalidateQueries({ queryKey: ['merchants', merchantId] });
      toast.success('Invitation re-sent successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to resend invitation')),
  });
};

export const useResendInviteById = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (merchantId: string) => merchantsApi.resendInvite(merchantId),
    onSuccess: (_data, merchantId) => {
      queryClient.invalidateQueries({ queryKey: ['merchants', merchantId] });
      queryClient.invalidateQueries({ queryKey: ['merchants'], exact: true });
      toast.success('Invitation re-sent successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to resend invitation')),
  });
};

export const useUpdateMerchantUser = (merchantId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateMerchantUserPayload }) =>
      merchantsApi.updateUser(userId, data),
    onSuccess: () => {
      // Refresh this merchant's user list so the edited email/name shows immediately.
      queryClient.invalidateQueries({ queryKey: ['merchants', merchantId, 'users'] });
      toast.success('User updated successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update user')),
  });
};

export const useDeleteMerchant = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => merchantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      toast.success('Merchant permanently deleted');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to delete merchant')),
  });
};
